/// <amd-module name="Widgets/SprintBurndownConfig/AnalyticsSprintBurndownConfig" />
import { Iteration } from "Analytics/Boards/BoardsQueries/IterationsQuery";
import { AnalyticsExceptionType, recognizeAnalyticsException } from "Analytics/Common/Utilities/AnalyticsExceptionUtilities";
import { isNameValid, NameField } from "Dashboards/CommonConfig/NameField";
import { SizePicker } from "Dashboards/CommonConfig/SizePicker";
import { ConfigProps, LoadingStatus } from "Dashboards/WidgetContracts/Internal/ConfigProps";
import { DashboardWidgetProps, WidgetGridSize } from "Dashboards/WidgetContracts/Internal/DashboardWidgetProps";
import * as React from "react";
import {
    AggregationPicker,
    AggregationSettings,
    ensureAggregationSettings,
    getAggregationOptions,
    WorkItemAggregationOption
} from "Reporting/BoardsCommon/Pickers/AggregationPicker";
import { ensureWorkItemFilter } from "Reporting/BoardsCommon/Pickers/BacklogCategoryPickerHelpers";
import { WorkItemBacklogFilter } from "Reporting/BoardsCommon/Pickers/WorkItemBacklogFilter";
import { convertBacklogInfoToFilter, IWorkItemBacklogInfo } from "Reporting/WorkItemsCategoriesPicker/IWorkItemBacklogInfo";
import { AllowedItems, getPickerId, getWorkItemAndCategoriesOptions } from "Reporting/WorkItemsCategoriesPicker/WorkItemsCategoriesHelper";
import { WorkItemsCategoriesPicker } from "Reporting/WorkItemsCategoriesPicker/WorkItemsCategoriesPicker";
import * as Core from "Tfs/Platform/Contracts/Core";
import { format } from "VSS/Core/Util/String";
import { VssComponent } from "VSS/Platform/Layout";
import { Checkbox } from "VSSUI/Checkbox";
import { DatePicker } from "VSSUI/DatePicker";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem } from "VSSUI/ListBox";
import { daysBetweenDates, parseIsoDateStringInLocalTime, toLocalDateIsoString } from "WidgetCommon/DateUtils/DateUtils";
import { AggregationMode } from "WidgetCommon/ModernWidgetTypes/CommonConfigurationTypes";
import { ModefulValueSetting } from "WidgetCommon/ModernWidgetTypes/ModefulValueSetting";
import { WidgetSettingsManagerProps } from "WidgetCommon/ModernWidgetTypes/SettingsManagerBase";
import { AnalyticsSprintBurndownSettings } from "Widgets/AnalyticsSprintBurndown/AnalyticsSprintBurndownWidgetComponents/AnalyticsSprintBurndownSettings";
import {
    AnalyticsSprintBurndownSettingsManager,
    assembleBurndownReportSettings,
    assembleTimePeriodConfiguration
} from "Widgets/AnalyticsSprintBurndown/AnalyticsSprintBurndownWidgetComponents/AnalyticsSprintBurndownSettingsManager";
import { isSymbolicCurrentIteration } from "Widgets/AnalyticsSprintBurndown/AnalyticsSprintBurndownWidgetComponents/ConfigQueries/IterationsHelper";
import { GenericSelectionDropdown } from "Widgets/Configs/Common/GenericSelectionDropdown";
import * as Resources from "Widgets/SprintBurndownConfig/Resources";
import { isLegacyFlagSuppressed } from "Widgets/SprintBurndownWidget/SprintBurndownSettingsManager";
import { renderTeamPicker, renderVersionToggle } from "./CommonConfigRenderer";
import { IToggleConfigMode } from "./IToggleConfigMode";
import { ensureIteration, getIterationItems } from "./Pickers/IterationsPickerHelper";
import { getTeamListItems } from "./Pickers/OpStoreTeamsPickerHelper";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { BurndownConstants } from "Widgets/Burndown/BurndownWidgetComponents/BurndownConstants";

interface AnalyticsBurndownConfigState {
    name: string;
    size: WidgetGridSize;

    widgetSettings: AnalyticsSprintBurndownSettings;

    initialLoadComplete: boolean;

    workItemsCategoriesOptions: IListBoxItem<IWorkItemBacklogInfo>[];
    aggregationOptions: WorkItemAggregationOption[];
    teamOptions: IListBoxItem<Core.WebApiTeam>[];
    iterationOptions: IListBoxItem<Iteration>[];
    hasPermissionsError?: boolean;
    hasValidationMessage: boolean;
}

interface AnalyticsBurndownFormFieldChanges{
    startDate :Date
    endDate :Date
    hasValidStartDate :boolean
    hasValidEndDate:boolean
    hasValidDates :boolean
    datesDifferenceAboveLimit:boolean
    datesOutOfOrder :boolean
    teamHasIterations :boolean
    isUsingCurrentIteration :boolean
}

export class AnalyticsSprintBurndownConfig extends VssComponent<ConfigProps & IToggleConfigMode, AnalyticsBurndownConfigState> {
    public static readonly remainingWorkField = "Microsoft.VSTS.Scheduling.RemainingWork";
    public static readonly componentType = "AnalyticsSprintBurndownConfig";
    public static readonly LegacySprintBurndownContributionId = "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.SprintBurndownWidget";

    constructor(props: ConfigProps & IToggleConfigMode) {
        super(props);
        this.state = {
            name: this.props.config.name,
            size: this.props.config.size,
            widgetSettings: {} as any,

            initialLoadComplete: false,

            workItemsCategoriesOptions: [],
            aggregationOptions: [],
            teamOptions: [],
            iterationOptions: [],
            hasPermissionsError: false,
            hasValidationMessage: false
        };
    }

    public async componentDidMount(): Promise<void> {
        try {
            const widgetProps = this.props.config as WidgetSettingsManagerProps;
            const returnIncompleteSettings = true;

            const settingsManager = new AnalyticsSprintBurndownSettingsManager(this.context.pageContext, this.props.teamContext);
            const widgetSettings = await settingsManager.ensureInitialSettings(widgetProps, returnIncompleteSettings);
            let title = this.state.name;

            // Step 1: Get Team Options
            const teams = await this.trackPromise(getTeamListItems(this.context.pageContext, widgetSettings.team.projectId)).promise;

            // One-time Fix up of legacy settings if this widget is being upgraded from legacy shape
            if (!isLegacyFlagSuppressed(this.props.config)) {
                // Legacy widget always rendered sprint Name - Override widget name to conform to template when upgrading.
                const teamIndex = teams.findIndex((team) => {
                    return team.id === widgetSettings.team.teamId;
                });
                const teamName = teams[teamIndex].text || "";
                title = format(Resources.WidgetTitleTemplate, teamName);
            }

            const teamDependentData = await this.ensureTeamDependentData(widgetSettings.team.projectId, widgetSettings.team.teamId, widgetSettings);

            const showNonWorkingDays = widgetSettings.showNonWorkingDays;
            const newSettings = assembleBurndownReportSettings(
                widgetSettings.team.projectId,
                widgetSettings.team.teamId,
                teamDependentData.activeWorkItemTypeFilter,
                widgetSettings.aggregation,
                teamDependentData.startDate,
                teamDependentData.endDate,
                teamDependentData.activeIteration,
                showNonWorkingDays!
            );
            // Restore simple non-default props
            newSettings.stackByWorkItemTypeEnabled = widgetSettings.stackByWorkItemTypeEnabled;
            newSettings.totalScopeTrendlineEnabled = widgetSettings.totalScopeTrendlineEnabled;

            // override value with 'false' if FF is disabled
            newSettings.showResolvedItemsAsCompletedEnabled = isFeatureFlagEnabled(
                this.context.pageContext,
                BurndownConstants.featureFlagName_ShowResolvedItemsAsCompleted, 
                false) ? widgetSettings.showResolvedItemsAsCompletedEnabled : false;

            this.setState(
                {
                    initialLoadComplete: true,
                    name: title,
                    widgetSettings: newSettings,
                    workItemsCategoriesOptions: teamDependentData.workItemCategoriesOptions,
                    aggregationOptions: teamDependentData.aggregationOptions,
                    teamOptions: teams,
                    iterationOptions: teamDependentData.iterationOptions
                },
                () => {
                    if (this.props.isReload) {
                        this.notifyParentOfUpdate();
                    } else {
                        this.props.parentProps.onConfigLoaded({ outcome: LoadingStatus.success }); // The configuration experience becomes visible after this point.
                    }
                }
            );
        } catch (e) {
            if (e.isCanceled) {
                return; // Do nothing if the promises were cancelled
            }
            if (recognizeAnalyticsException(e) === AnalyticsExceptionType.MissingAnalyticsPermissions) {
                if (this.props.config.contributionId === AnalyticsSprintBurndownConfig.LegacySprintBurndownContributionId) {
                    this.setState({
                        hasPermissionsError: true
                    });
                    this.props.parentProps.onConfigLoaded({ outcome: LoadingStatus.success });
                    return;
                } else {
                    // Something went wrong. Now an error is presented.
                    this.props.parentProps.onConfigLoaded({
                        outcome: LoadingStatus.failure,
                        outcomeDescription: Resources.AccessDeniedSummary
                    });
                }
            } else if (recognizeAnalyticsException(e) === AnalyticsExceptionType.Disabled) {
                this.props.onToggleConfig(true);
                this.props.parentProps.onConfigLoaded({ outcome: LoadingStatus.success });
                return;
            }
            // Something went wrong. Now an error is presented.
            this.props.parentProps.onConfigLoaded({
                outcome: LoadingStatus.failure,
                outcomeDescription: e
            });
        }
    }

    /** Get or repopulate bits which can be impacted by team selection changes.
     * Note: PriorWidgetSettings are only passed in for initial load. We need to ignore prior config when reacting to upstream team selection event.
     */
    public async ensureTeamDependentData(
        projectId: string,
        teamId: string,
        priorWidgetSettings?: AnalyticsSprintBurndownSettings
    ): Promise<{
        workItemCategoriesOptions: IListBoxItem<IWorkItemBacklogInfo>[];
        aggregationOptions: WorkItemAggregationOption[];
        aggregationSettings: AggregationSettings;
        iterationOptions: IListBoxItem<Iteration>[];
        startDate?: Date;
        endDate?: Date;
        activeWorkItemTypeFilter: WorkItemBacklogFilter;
        activeIteration: Iteration;
    }> {
        // Step 2: Get WIT Options & Active filter
        const workItemsCategoriesOptions = await getWorkItemAndCategoriesOptions(
            this.context.pageContext,
            projectId,
            teamId,
            () => {
                return true;
            },
            AllowedItems.BacklogAndWorkItems
        );

        const activeWorkItemTypeFilter: WorkItemBacklogFilter = await this.trackPromise(
            ensureWorkItemFilter(this.context.pageContext, priorWidgetSettings && (priorWidgetSettings.workItemTypeFilter as WorkItemBacklogFilter))
        ).promise;

        // Step 3: Get active and available set of aggregation options by workitem in a backlog category or specific work item type
        const aggregationOptions = await this.getAggregationOptions(projectId, teamId, activeWorkItemTypeFilter);
        const aggregationSettings = ensureAggregationSettings(
            aggregationOptions,
            (priorWidgetSettings != null && priorWidgetSettings.aggregation) || undefined
        );

        // Step 4 - Get info about active and available Iteration Options (No-op, selection is Handled by parent)
        const iterations = await this.trackPromise(getIterationItems(this.context.pageContext, projectId, teamId)).promise;
        // Note: Teams can have no available iterations
        const activeIteration = ensureIteration(iterations, priorWidgetSettings && priorWidgetSettings.iterationId);
        const { startDate, endDate } = this.ensureDates(activeIteration, priorWidgetSettings);

        return {
            workItemCategoriesOptions: workItemsCategoriesOptions,
            aggregationOptions: aggregationOptions,
            aggregationSettings: aggregationSettings,
            iterationOptions: iterations,
            startDate: startDate,
            endDate: endDate,
            activeWorkItemTypeFilter: activeWorkItemTypeFilter,
            activeIteration: activeIteration
        };
    }

    public async getAggregationOptions(
        projectId: string,
        teamId: string,
        activeWorkItemTypeFilter: WorkItemBacklogFilter
    ): Promise<WorkItemAggregationOption[]> {
        return this.trackPromise(getAggregationOptions(this.context.pageContext, projectId, teamId, activeWorkItemTypeFilter)).promise;
    }

    public componentDidUpdate(prevProps: Readonly<ConfigProps>): void {
        if (prevProps.saveValidationTimeStamp !== this.props.saveValidationTimeStamp) {
            const currentConfig = this.props.config;
            this.props.parentProps.onValidationResponse(currentConfig, this.isConfigValid(currentConfig));
        }
    }
    /*to get the latest form validation state*/
    public checkValidationState() {
        const formChanges = this.getFormChanges();
        if (
            (formChanges.datesOutOfOrder && formChanges.datesDifferenceAboveLimit) ||
            !formChanges.teamHasIterations ||
            (formChanges.isUsingCurrentIteration && !formChanges.hasValidDates)
        ) {
            return true;
        } else {
            return false;
        }
    }
    
    /*to get the latest form field changes*/
    public getFormChanges() {
        const formValues = {} as AnalyticsBurndownFormFieldChanges;
        formValues.startDate = parseIsoDateStringInLocalTime(this.state.widgetSettings.timePeriodConfiguration.startDate);
        formValues.endDate = parseIsoDateStringInLocalTime(this.state.widgetSettings.timePeriodConfiguration.endDate);
        formValues.hasValidStartDate = !isNaN(formValues.startDate.getTime());
        formValues.hasValidEndDate = !isNaN(formValues.endDate.getTime());
        formValues.hasValidDates = formValues.hasValidStartDate && formValues.hasValidEndDate;
        formValues.datesDifferenceAboveLimit = daysBetweenDates(formValues.startDate, formValues.endDate) > 180;
        formValues.datesOutOfOrder = formValues.startDate.getTime() >= formValues.endDate.getTime();
        formValues.teamHasIterations = this.state.iterationOptions != null && this.state.iterationOptions.length > 1; // Does the team have any iterations other than @currentIteration?
        formValues.isUsingCurrentIteration = isSymbolicCurrentIteration(this.state.widgetSettings.iterationId);
        return formValues;
    }

    public render(): JSX.Element | null {
        if (this.state.hasPermissionsError) {
            return <div className="sprint-burndown-config"> {this.renderMinimalConfigExperience()}</div>;
        }

        if (!this.state.initialLoadComplete) {
            return null;
        }

        const formValues = this.getFormChanges();
        return (
            <div className="sprint-burndown-config">
                <NameField value={this.state.name} onChange={this.onNameChanged} />
                {this.props.contributionConstraints.supportedSizes.length > 1 && (
                    <SizePicker
                        key="size-picker"
                        onChange={this.onSizeChanged}
                        sizes={this.props.contributionConstraints.supportedSizes}
                        value={this.props.config.size}
                    />
                )}
                {renderTeamPicker(this.onTeamSelect, this.state.teamOptions, this.state.widgetSettings!.team!.teamId)}
                {this.renderMinimalConfigExperience()}
                <FormItem label={Resources.BacklogsAndWorkItemsLabel} className="widget-config-form-item">
                    <WorkItemsCategoriesPicker
                        items={this.state.workItemsCategoriesOptions}
                        selectedIds={[
                            getPickerId(
                                this.state.widgetSettings.workItemTypeFilter.identifier,
                                this.state.widgetSettings.workItemTypeFilter.settings
                            )
                        ]}
                        onSelect={this.onSelectWorkItemFilter}
                        suppressMultiSelect={true}
                    />
                </FormItem>
                <FormItem label={Resources.BurndownOnLabel} className="widget-config-form-item">
                    <AggregationPicker
                        value={this.state.widgetSettings.aggregation}
                        items={this.state.aggregationOptions}
                        onSelect={this.onAggregationSelect}
                    />
                </FormItem>
                <FormItem
                    label={Resources.SelectIterationLabel}
                    className="widget-config-form-item"
                    error={!formValues.teamHasIterations || (formValues.isUsingCurrentIteration && !formValues.hasValidDates)}
                    message={
                        (!formValues.teamHasIterations && Resources.NoIterationsFoundError) ||
                        (formValues.isUsingCurrentIteration && !formValues.hasValidDates && Resources.CurrentIterationCouldNotBeFound)
                    }
                >
                    <GenericSelectionDropdown
                        className="iteration-picker"
                        items={formValues.teamHasIterations ? this.state.iterationOptions : []}
                        selectedId={formValues.teamHasIterations ? this.state.widgetSettings.iterationId : undefined}
                        onSelect={this.onIterationSelect}
                    />
                </FormItem>
                <FormItem
                    label={Resources.TimePeriodLabel}
                    className="widget-config-form-item "
                    error={formValues.datesOutOfOrder || formValues.datesDifferenceAboveLimit}
                    message={
                        (formValues.datesOutOfOrder && Resources.DatesOutOfOrderError) ||
                        (formValues.datesDifferenceAboveLimit && Resources.DateRangeExceedsLimit)
                    }
                >
                    <FormItem label={Resources.StartDateLabel} className="flex-row widget-config-left-label">
                        <DatePicker
                            className={"widget-date-picker"}
                            ariaLabel={Resources.StartDateLabel}
                            value={formValues.startDate && formValues.hasValidStartDate ? formValues.startDate : undefined}
                            onChange={this.onStartDateSelect}
                            disabled={!formValues.hasValidStartDate || formValues.isUsingCurrentIteration}
                        />
                    </FormItem>
                    <FormItem label={Resources.EndDateLabel} className="flex-row widget-config-left-label">
                        <DatePicker
                            className={"widget-date-picker"}
                            ariaLabel={Resources.EndDateLabel}
                            value={formValues.endDate && formValues.hasValidEndDate ? formValues.endDate : undefined}
                            onChange={this.onEndDateSelect}
                            disabled={!formValues.hasValidEndDate || formValues.isUsingCurrentIteration}
                        />
                    </FormItem>
                </FormItem>
                <FormItem label={Resources.AdvancedFeaturesLabel}>
                    <>
                        <Checkbox
                            label={Resources.ShowTotalScopeTrendlineLabel}
                            onChange={this.onToggleTotalScope}
                            checked={this.state.widgetSettings.totalScopeTrendlineEnabled}
                            disabled={this.isUsingRemainingWork(this.state.widgetSettings)}
                        />
                        <Checkbox
                            label={Resources.ShowNonWorkingDaysLabel}
                            onChange={this.onToggleNonWorkingDays}
                            checked={this.state.widgetSettings.showNonWorkingDays}
                        />
                        <Checkbox
                            label={Resources.PlotRemainingWorkLabel}
                            onChange={this.onToggleColorByWorkItem}
                            checked={this.state.widgetSettings.stackByWorkItemTypeEnabled}
                        />

                        {
                            isFeatureFlagEnabled(this.context.pageContext,BurndownConstants.featureFlagName_ShowResolvedItemsAsCompleted, false) &&
                            <Checkbox
                                label={this.renderCheckboxLabelWithInfoIcon(Resources.ShowResolvedItemsAsCompletedLabel)}
                                onChange={this.onToggleShowResolvedItemsAsCompleted}
                                checked={this.state.widgetSettings.showResolvedItemsAsCompletedEnabled}
                                tooltipProps={{text: Resources.ShowResolvedItemsAsCompletedLabelToolTipText}}
                            />
                        }       
                    </>
                </FormItem>
            </div>
        );
    }

    /* render ReactNode with labelText and an info icon at end of the text */
    private renderCheckboxLabelWithInfoIcon = (labelText: string) : React.ReactNode => {
        return (
            <div>
                <span>{labelText}</span> &#9432;
            </div>
        )
    }

    // Renders basic config with  toggle to upgrade along with error message
    public renderMinimalConfigExperience(): JSX.Element | null {
        const canToggleDowngrade = this.props.onToggleConfig != null;

        return (
            <>
                {canToggleDowngrade &&
                    renderVersionToggle(
                        this.onToggleLegacyMode,
                        true /*isInModernMode*/,
                        undefined,
                        this.state.hasPermissionsError ? Resources.AccessDeniedSummary : undefined
                    )}
            </>
        );
    }

    private ensureDates(
        activeIteration: Iteration,
        priorWidgetSettings?: AnalyticsSprintBurndownSettings
    ): { startDate: Date | undefined; endDate: Date | undefined } {
        let startDate = undefined;
        let endDate = undefined;
        if (isValidIterationForDates(activeIteration)) {
            // Note Ax Service returns start date-time as the first moment of the first day, and end date as last moment of the last day
            // As opposed to the same time on first and last dates, as the Sprint Burndown report gets from Boards description of iterations.
            // The parse method below interprets both formats in uniform fashion by only extracting the date components, ignoring time component.
            startDate = parseIsoDateStringInLocalTime(activeIteration.StartDateTimeOffset);
            endDate = parseIsoDateStringInLocalTime(activeIteration.EndDateTimeOffset);

            if (priorWidgetSettings && priorWidgetSettings.timePeriodConfiguration && !isSymbolicCurrentIteration(priorWidgetSettings.iterationId)) {
                startDate = parseIsoDateStringInLocalTime(priorWidgetSettings.timePeriodConfiguration.startDate);
                endDate = parseIsoDateStringInLocalTime(priorWidgetSettings.timePeriodConfiguration.endDate);
            }
        }
        return {
            startDate: startDate,
            endDate: endDate
        };
    }

    private isConfigValid(props: DashboardWidgetProps): boolean {
        return isNameValid(props.name) && AnalyticsSprintBurndownSettingsManager.settingsAreValid(this.state.widgetSettings);
    }

    /** EVENT HANDLERS */
    /* Pass Name changes up to parent. */
    private onNameChanged = (newName: string): void => {
        this.setState({ name: newName }, this.notifyParentOfUpdate);
    };

    /* Pass Size changes up to parent. */
    private onSizeChanged = (newSize: WidgetGridSize): void => {
        this.setState({ size: newSize }, this.notifyParentOfUpdate);
    };

    private onTeamSelect = async (teamItem: IListBoxItem<Core.WebApiTeam>): Promise<void> => {
        const newName = format(Resources.WidgetTitleTemplate, teamItem.text);
        const newSettings = { ...this.state.widgetSettings };
        newSettings.team.teamId = teamItem.id;
        const teamDependentData = await this.ensureTeamDependentData(newSettings.team.projectId, newSettings.team.teamId);

        newSettings.iterationId = teamDependentData.activeIteration.IterationSK;
        newSettings.iterationPath = teamDependentData.activeIteration.IterationPath;
        newSettings.workItemTypeFilter = teamDependentData.activeWorkItemTypeFilter;
        newSettings.aggregation = teamDependentData.aggregationSettings;
        newSettings.timePeriodConfiguration.startDate = this.attemptToPackDate(teamDependentData.startDate);
        newSettings.timePeriodConfiguration.endDate = this.attemptToPackDate(teamDependentData.endDate);

        const localState = {
            initialLoadComplete: true,
            widgetSettings: newSettings,

            workItemsCategoriesOptions: teamDependentData.workItemCategoriesOptions,
            aggregationOptions: teamDependentData.aggregationOptions,
            iterationOptions: teamDependentData.iterationOptions,
            name: newName
        } as AnalyticsBurndownConfigState;
        this.setState(localState, this.notifyParentOfUpdate);
    };

    private attemptToPackDate(date: Date | undefined): string {
        return date != null && !isNaN(date.getTime()) ? toLocalDateIsoString(date) : "";
    }

    private onSelectWorkItemFilter = async (workItemFilter: IListBoxItem<IWorkItemBacklogInfo>[]): Promise<void> => {
        const newSettings = { ...this.state.widgetSettings };

        // Convert Picker format to simpler settings format.
        const filterSettings: WorkItemBacklogFilter = convertBacklogInfoToFilter(workItemFilter[0].data!);

        const teamInfo = this.state.widgetSettings.team;
        const aggregationOptions = await this.getAggregationOptions(teamInfo.projectId, teamInfo.teamId, filterSettings);
        const aggregationSettings = ensureAggregationSettings(aggregationOptions);
        newSettings.workItemTypeFilter = filterSettings;
        newSettings.aggregation = aggregationSettings;
        newSettings.isCustomized = true;
        this.setState({ widgetSettings: newSettings, aggregationOptions: aggregationOptions }, this.notifyParentOfUpdate);
    };

    private onAggregationSelect = (newAggregationField: AggregationSettings): void => {
        const newSettings = { ...this.state.widgetSettings };
        newSettings.aggregation = newAggregationField as ModefulValueSetting<AggregationMode, string>;
        newSettings.isCustomized = true;
        this.updateWidgetSettings(newSettings);
    };

    private onIterationSelect = (iterationItem: IListBoxItem<Iteration>): void => {
        const newSettings = { ...this.state.widgetSettings };
        newSettings.iterationId = iterationItem.data!.IterationSK;
        newSettings.iterationPath = iterationItem.data!.IterationPath;
        const { startDate, endDate } = this.ensureDates(iterationItem.data!); // Note: We only pass iteration in - Any prior date settings are ignored in this path.
        newSettings.timePeriodConfiguration = assembleTimePeriodConfiguration(startDate, endDate);
        newSettings.isCustomized = true;
        this.updateWidgetSettings(newSettings);
    };

    private onStartDateSelect = (newStartDate: Date): void => {
        const newSettings = { ...this.state.widgetSettings };
        newSettings.timePeriodConfiguration.startDate = toLocalDateIsoString(newStartDate);
        newSettings.isCustomized = true;
        this.updateWidgetSettings(newSettings);
    };

    private onEndDateSelect = (newEndDate: Date): void => {
        const newSettings = { ...this.state.widgetSettings };
        newSettings.timePeriodConfiguration.endDate = toLocalDateIsoString(newEndDate);
        newSettings.isCustomized = true;
        this.updateWidgetSettings(newSettings);
    };

    private onToggleNonWorkingDays = (unusedParam: any, checked: boolean): void => {
        const newSettings = { ...this.state.widgetSettings };
        newSettings.showNonWorkingDays = checked;
        newSettings.isCustomized = true;
        this.updateWidgetSettings(newSettings);
    };

    private onToggleTotalScope = (unusedParam: any, checked: boolean): void => {
        const newSettings = { ...this.state.widgetSettings };
        newSettings.totalScopeTrendlineEnabled = checked;
        newSettings.isCustomized = true;
        this.updateWidgetSettings(newSettings);
    };

    private onToggleColorByWorkItem = (unusedParam: any, checked: boolean): void => {
        const newSettings = { ...this.state.widgetSettings };
        newSettings.stackByWorkItemTypeEnabled = checked;
        newSettings.isCustomized = true;
        this.updateWidgetSettings(newSettings);
    };

    private onToggleShowResolvedItemsAsCompleted = (unusedParam: any, checked: boolean): void => {
        const newSettings = { ...this.state.widgetSettings };
        newSettings.showResolvedItemsAsCompletedEnabled = checked;
        newSettings.isCustomized = true;
        this.updateWidgetSettings(newSettings);
    };

    private onToggleLegacyMode = (): void => {
        // Request a switch to the legacy config
        this.props.onToggleConfig(true);
    };

    /** Misc helpers */

    private updateWidgetSettings(widgetSettings: AnalyticsSprintBurndownSettings): void {
        if (this.isUsingRemainingWork(widgetSettings)) {
            widgetSettings.totalScopeTrendlineEnabled = false;
        }

        this.setState({ widgetSettings: widgetSettings }, this.notifyParentOfUpdate);
    }

    private notifyParentOfUpdate = (): void => {
        const updatedConfig = {
            ...this.props.config,
            name: this.state.name,
            size: this.state.size,
            hasError: this.checkValidationState(),
            settings: JSON.stringify(this.state.widgetSettings)
        };
        this.props.parentProps.onUpdate(updatedConfig);
    };

    private isUsingRemainingWork(widgetSettings: AnalyticsSprintBurndownSettings) {
        return widgetSettings.aggregation.settings === AnalyticsSprintBurndownConfig.remainingWorkField;
    }
}

VssComponent.register(AnalyticsSprintBurndownConfig.componentType, AnalyticsSprintBurndownConfig);

function isValidIterationForDates(iteration: Iteration): boolean {
    return iteration != null && iteration.StartDateTimeOffset != null && iteration.StartDateTimeOffset != "";
}
